// Snooker table geometry, initial layout and frame rules.
//
// SHARED FILE: the canonical copy lives in express-server/game/rules.ts and is copied to
// client/src/shared/rules.ts by `npm run sync-shared` in client/ (also run automatically before
// `npm start` / `npm run build`). Edit the express-server copy only.
//
// Pure TypeScript, no imports, no Node or browser APIs, so the server (authoritative rules)
// and the client (practice mode, HUD, ball-in-hand placement) run exactly the same code.
//
// Coordinates are the physics plane of the game: x across the table, y along it
// (rendered as three.js z). The baulk end is at negative y.

export type Vec = [number, number];
export type Positions = Record<string, Vec>;

export const COLOURS = ["yellow", "green", "brown", "blue", "pink", "black"] as const;
export type Colour = typeof COLOURS[number];
export type BallOn = "red" | "colour" | Colour | "any";

// Rulesets. "snooker" is the full game (the default: a state or a client without a ruleset is snooker).
// "anyball": every object ball is on and worth 1, a pot keeps the turn, a scratch or a miss costs the shooter 1,
// nothing is re-spotted, and the frame ends when only the cue ball is left (higher score wins; a tie re-spots the
// 8 ball for sudden death). It is played with the 15 numbered balls (POOL_IDS); numbers and colours don't matter.
export const RULESETS = ["snooker", "anyball"] as const;
export type Ruleset = typeof RULESETS[number];
export const RULESET_NAMES: Record<Ruleset, string> = {snooker: "Snooker", anyball: "Any ball"};
export const isRuleset = (v: unknown): v is Ruleset => typeof v === "string" && (RULESETS as readonly string[]).includes(v);
/** The ruleset of a frame (a frame without one, e.g. from an older server, is snooker). */
export const rulesetOf = (state: {ruleset?: unknown} | null | undefined): Ruleset => isRuleset(state?.ruleset) ? state!.ruleset as Ruleset : "snooker";

export const BALL_RADIUS = 0.626514 / 2;

// Playing area inside the cushions: the straight parts of CUSHION_LINE below.
export const TABLE = {
    xMin: -5.00424,
    xMax: 3.12927,
    yMin: -6.62363,
    yMax: 6.70471,
};
const CX = (TABLE.xMin + TABLE.xMax) / 2;

// The cushion line: the closed polyline of walls the balls bounce off (client/src/physics/table.ts builds the
// physics walls and their corner/jaw tips, and the 3D table its cushion noses and pocket liners, from this list). It follows the four cushions (the TABLE
// rectangle above) and, at each pocket, the jaws and the back of the pocket. The point
// [2.46634, -6.62363] is listed twice on purpose: the original wall list had it twice (a zero-length edge), and
// keeping it keeps the outline exactly as it was (the physics skips the zero-length edge).
export const CUSHION_LINE: Vec[] = [
    [3.12927, -5.96507], [3.13541, -0.6216], [3.25568, -0.51961], [3.50969, -0.48805],
    [3.63418, -0.4197], [3.72532, -0.32153], [3.82741, -0.15508], [3.87626, 0.04054],
    [3.82741, 0.23615], [3.72532, 0.40261], [3.63418, 0.50078], [3.25568, 0.60069],
    [3.13541, 0.70268], [3.12927, 6.04614], [3.18432, 6.21105], [3.30333, 6.33524],
    [3.49906, 6.41607], [3.63035, 6.58829], [3.66284, 6.74668], [3.62979, 6.92077],
    [3.54807, 7.06928], [3.42948, 7.15335], [3.32469, 7.18972], [3.14506, 7.18455],
    [2.94553, 7.10788], [2.75544, 6.87877], [2.63125, 6.75976], [2.46634, 6.70471],
    [-4.34131, 6.70471], [-4.50622, 6.75976], [-4.63041, 6.87877], [-4.8205, 7.10788],
    [-5.02003, 7.18455], [-5.19966, 7.18972], [-5.30444, 7.15335], [-5.42304, 7.06928],
    [-5.50476, 6.92077], [-5.53781, 6.74668], [-5.50532, 6.58829], [-5.37403, 6.41607],
    [-5.1783, 6.33524], [-5.05929, 6.21105], [-5.00424, 6.04614], [-5.01038, 0.68663],
    [-5.1434, 0.58464], [-5.41461, 0.55307], [-5.54753, 0.48473], [-5.64484, 0.38656],
    [-5.75384, 0.2201], [-5.806, 0.04054], [-5.75384, -0.15508], [-5.64484, -0.32153],
    [-5.54753, -0.4197], [-5.41461, -0.48805], [-5.1434, -0.51961], [-5.01038, -0.6216],
    [-5.00424, -5.96507], [-5.05929, -6.12997], [-5.1783, -6.25417], [-5.37403, -6.335],
    [-5.50532, -6.50721], [-5.53781, -6.66561], [-5.50476, -6.8397], [-5.42304, -6.98821],
    [-5.30444, -7.07227], [-5.19966, -7.10865], [-5.02003, -7.10348], [-4.8205, -7.02681],
    [-4.63041, -6.79769], [-4.50622, -6.67869], [-4.34131, -6.62363], [2.46634, -6.62363],
    [2.46634, -6.62363], [2.63125, -6.67869], [2.75544, -6.79769], [2.94553, -7.02681],
    [3.14506, -7.10348], [3.32469, -7.10865], [3.42948, -7.07227], [3.54807, -6.98821],
    [3.62979, -6.8397], [3.66284, -6.66561], [3.63035, -6.50721], [3.49906, -6.335],
    [3.30333, -6.25417], [3.18432, -6.12997], [3.12927, -5.96507],
];

// Baulk line at ~1/5 of the length from the baulk cushion, D radius ~1/6 of the width,
// as on a full-size table. The table/ball ratio is closer to pool than to snooker, so the
// pink/black spots and the pyramid are moved slightly towards the centre to fit 15 reds.
export const BAULK_Y = TABLE.yMin + 0.2065 * (TABLE.yMax - TABLE.yMin);
export const D_RADIUS = 0.164 * (TABLE.xMax - TABLE.xMin);
export const D_CENTRE: Vec = [CX, BAULK_Y];

// Seen from the baulk end the yellow is on the left of the D: that is +x in this scene.
export const SPOTS: Record<Colour, Vec> = {
    yellow: [CX + D_RADIUS, BAULK_Y],
    green: [CX - D_RADIUS, BAULK_Y],
    brown: [CX, BAULK_Y],
    blue: [CX, 0.04],
    pink: [CX, 2.0],
    black: [CX, 5.75],
};

export const VALUES: Record<string, number> = {
    red: 1, yellow: 2, green: 3, brown: 4, blue: 5, pink: 6, black: 7,
};

export const RED_IDS = Array.from({length: 15}, (_, i) => `red${i + 1}`);
// Any ball is played with a set of 15 numbered pool-style balls, ball1..ball15 (1-8 solids, 9-15 stripes). The
// numbers and colours are only looks: every one is an equal object ball worth 1.
export const POOL_IDS = Array.from({length: 15}, (_, i) => `ball${i + 1}`);
/** Every ball the client may show: the cue ball, the snooker set and the numbered set. */
export const ALL_BALL_IDS = ["cue", ...RED_IDS, ...COLOURS, ...POOL_IDS];

export const isRed = (id: string | null | undefined) => !!id && id.startsWith("red");
export const isColour = (id: string | null | undefined): id is Colour => !!id && (COLOURS as readonly string[]).includes(id);
export const isPoolBall = (id: string | null | undefined) => !!id && /^ball([1-9]|1[0-5])$/.test(id);
/** The number on a pool-style ball (1-15), or 0 for any other id. */
export const poolNumber = (id: string) => isPoolBall(id) ? Number(id.slice(4)) : 0;
export const ballValue = (id: string) => isRed(id) ? 1 : (VALUES[id] || 0);
export const ballName = (id: string) => isRed(id) ? "red" : id === "cue" ? "cue ball" : isPoolBall(id) ? `${poolNumber(id)} ball` : id;

// The Any ball rack, row by row from the apex: the 1 at the apex, the 8 in the middle of the third row and a
// stripe and a solid in the back corners, as a pool rack is usually set up (for looks only).
export const POOL_RACK: number[][] = [[1], [9, 2], [10, 8, 3], [4, 14, 7, 11], [12, 6, 15, 13, 5]];

/** The 15-ball triangle (apex towards baulk), the same place and spacing for the reds and for the numbered set. */
function rack(ids: string[], p: Positions) {
    const gap = 0.004;
    const dx = 2 * BALL_RADIUS + gap;
    const dy = (2 * BALL_RADIUS + gap) * Math.sqrt(3) / 2;
    const apexY = SPOTS.pink[1] + 2 * BALL_RADIUS + 0.02;
    let n = 0;
    for (let row = 0; row < 5; row++) {
        for (let j = 0; j <= row; j++) {
            p[ids[n++]] = [round(CX + (j - row / 2) * dx), round(apexY + row * dy)];
        }
    }
}

/** Snooker: cue ball, 15 reds and the colours on their spots. Any ball: cue ball and the 15 numbered balls racked. */
export function initialPositions(ruleset: Ruleset = "snooker"): Positions {
    const p: Positions = {};
    p.cue = [CX + 0.7, BAULK_Y - 0.55]; // between brown and yellow, clear of the brown and blue
    if (ruleset === "anyball") {
        rack(([] as number[]).concat(...POOL_RACK).map(n => `ball${n}`), p);
        return p;
    }
    for (const c of COLOURS) p[c] = [SPOTS[c][0], SPOTS[c][1]];
    rack(RED_IDS, p);
    return p;
}

function round(v: number) {
    return Math.round(v * 10000) / 10000;
}

function dist(a: Vec, b: Vec) {
    return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

export function inBounds(p: Vec, tolerance = 0.05) {
    return p[0] >= TABLE.xMin + BALL_RADIUS - tolerance && p[0] <= TABLE.xMax - BALL_RADIUS + tolerance
        && p[1] >= TABLE.yMin + BALL_RADIUS - tolerance && p[1] <= TABLE.yMax - BALL_RADIUS + tolerance;
}

function insidePolygon(p: Vec, poly: Vec[]) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[i], b = poly[j];
        if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < (b[0] - a[0]) * (p[1] - a[1]) / (b[1] - a[1]) + a[0]) inside = !inside;
    }
    return inside;
}

/** Distance from a point to the nearest wall of the cushion line. */
export function cushionClearance(p: Vec) {
    let best = Infinity;
    for (let i = 0; i < CUSHION_LINE.length - 1; i++) {
        const a = CUSHION_LINE[i], b = CUSHION_LINE[i + 1];
        const dx = b[0] - a[0], dy = b[1] - a[1];
        const len2 = dx * dx + dy * dy;
        const t = len2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)) : 0;
        best = Math.min(best, Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy));
    }
    return best;
}

/**
 * A ball centre the table can hold: inside the cushion rectangle (inBounds), or anywhere else inside the cushion
 * line where the ball doesn't overlap a wall (more than the tolerance). The second case is a ball that stopped in
 * the mouth of a pocket, past the cushion line but short of the pocket (the client pots a ball when its centre
 * enters the pocket polygon, client/src/tableGeometry.ts). At the middle pockets that strip is up to ~0.16 deep.
 */
export function onTable(p: Vec, tolerance = 0.05) {
    if (inBounds(p, tolerance)) return true;
    return insidePolygon(p, CUSHION_LINE) && cushionClearance(p) >= BALL_RADIUS - tolerance;
}

/** The balls of a report that aren't on the table (onTable), with their positions, for the server log. */
export function offTableBalls(positions: unknown): {id: string, position: unknown}[] {
    if (!positions || typeof positions !== "object") return [];
    const out: {id: string, position: unknown}[] = [];
    for (const [id, p] of Object.entries(positions as Record<string, unknown>)) {
        const ok = Array.isArray(p) && p.length === 2 && p.every(v => typeof v === "number" && isFinite(v)) && onTable(p as Vec);
        if (!ok) out.push({id, position: p});
    }
    return out;
}

export function isFree(positions: Positions, p: Vec, except?: string) {
    for (const id in positions) {
        if (id === except) continue;
        if (dist(positions[id], p) < 2 * BALL_RADIUS + 0.002) return false;
    }
    return true;
}

export function inD(p: Vec, tolerance = 0.002) {
    return p[1] <= BAULK_Y + tolerance && dist(p, D_CENTRE) <= D_RADIUS + tolerance && inBounds(p, 0);
}

/** Clamp a point into the D (used while placing the cue ball in hand). */
export function clampToD(p: Vec): Vec {
    let x = p[0], y = Math.min(p[1], BAULK_Y);
    const d = dist([x, y], D_CENTRE);
    if (d > D_RADIUS) {
        x = D_CENTRE[0] + (x - D_CENTRE[0]) * D_RADIUS / d;
        y = D_CENTRE[1] + (y - D_CENTRE[1]) * D_RADIUS / d;
    }
    y = Math.max(y, TABLE.yMin + BALL_RADIUS);
    return [x, y];
}

/** A free cue-ball position in the D (preferred spot first). */
export function defaultCuePosition(positions: Positions): Vec {
    // Default: between brown and yellow, behind the line, with a clear straight path past the blue and pink.
    const candidates: Vec[] = [[CX + 0.7, BAULK_Y - 0.55], [CX - 0.7, BAULK_Y - 0.55], [CX, BAULK_Y - 0.8]];
    for (let r = 0.2; r <= D_RADIUS; r += 0.2) {
        for (let a = 0; a < 360; a += 30) {
            const rad = a * Math.PI / 180;
            candidates.push([CX + r * Math.cos(rad), BAULK_Y - Math.abs(r * Math.sin(rad))]);
        }
    }
    for (const c of candidates) {
        if (inD(c) && isFree(positions, c, "cue")) return [round(c[0]), round(c[1])];
    }
    return [CX, BAULK_Y - 0.5];
}

/**
 * Respot a colour: its own spot if free, otherwise the highest-value free spot, otherwise as
 * near as possible to its own spot towards the top cushion (then towards baulk).
 */
export function respotPosition(colour: Colour, positions: Positions): Vec {
    const own = SPOTS[colour];
    if (isFree(positions, own, colour)) return [own[0], own[1]];
    for (const c of COLOURS.slice().reverse()) {
        if (isFree(positions, SPOTS[c], colour)) return [SPOTS[c][0], SPOTS[c][1]];
    }
    for (const dir of [1, -1]) {
        for (let y = own[1]; y > TABLE.yMin + BALL_RADIUS && y < TABLE.yMax - BALL_RADIUS; y += dir * 0.01) {
            const p: Vec = [own[0], round(y)];
            if (isFree(positions, p, colour)) return p;
        }
    }
    return [own[0], own[1]];
}

export interface PlayerFrame {
    score: number;
    highestBreak: number;
    fouls: number;
}

export interface FrameState {
    positions: Positions;     // balls on the table
    ballOn: BallOn;
    turn: number;             // index of the player to shoot
    players: number;          // 1 = practice, 2 = match
    stats: PlayerFrame[];
    breakPoints: number;      // current break of the player at the table
    ballInHand: boolean;      // the player to shoot may place the cue ball in the D
    shots: number;
    breaker: number;
    respottedBlack: boolean;
    over: boolean;
    winner: number | null;
    endReason: string | null;
    ruleset?: Ruleset;        // missing = snooker (states from before rulesets)
    suddenDeath?: boolean;    // anyball: the scores were level with the table cleared, the 8 ball is re-spotted
}

export interface ShotReport {
    firstHit: string | null;  // first object ball the cue ball touched
    potted: string[];         // in the order they dropped (may include "cue")
    positions: Positions;     // final positions of every ball still on the table
    cueStart?: Vec;           // cue ball position at the moment of the shot
}

export interface ShotSummary {
    player: number;
    foul: boolean;
    foulReasons: string[];
    penalty: number;
    points: number;
    potted: string[];
    respotted: string[];
    turnChanged: boolean;
    ballInHand: boolean;
    frameOver: boolean;
    message: string;
}

export function newFrame(players = 2, breaker = 0, ruleset: Ruleset = "snooker"): FrameState {
    const anyball = ruleset === "anyball";
    return {
        positions: initialPositions(ruleset),
        ballOn: anyball ? "any" : "red",
        turn: breaker,
        players,
        stats: Array.from({length: players}, () => ({score: 0, highestBreak: 0, fouls: 0})),
        breakPoints: 0,
        ballInHand: true,
        shots: 0,
        breaker,
        respottedBlack: false,
        over: false,
        winner: null,
        endReason: null,
        ruleset,
        suddenDeath: false,
    };
}

export function redsOnTable(positions: Positions) {
    return Object.keys(positions).filter(isRed).length;
}

/** Object balls (everything but the cue ball) still on the table. */
export function objectBallsOnTable(positions: Positions) {
    return Object.keys(positions).filter(id => id !== "cue").length;
}

export function lowestColourOnTable(positions: Positions): Colour | null {
    for (const c of COLOURS) if (positions[c]) return c;
    return null;
}

export function describeBallOn(ballOn: BallOn) {
    return ballOn === "colour" ? "any colour" : ballOn === "any" ? "any ball" : ballOn;
}

function ballOnValue(ballOn: BallOn, firstHit: string | null) {
    if (ballOn === "red") return 1;
    if (ballOn === "colour") return isColour(firstHit) ? ballValue(firstHit) : 0;
    return VALUES[ballOn];
}

function legalFirstHit(ballOn: BallOn, id: string) {
    if (ballOn === "red") return isRed(id);
    if (ballOn === "colour") return isColour(id);
    return id === ballOn;
}

function legalPot(ballOn: BallOn, firstHit: string | null, id: string) {
    if (ballOn === "red") return isRed(id);
    if (ballOn === "colour") return isColour(id) && id === firstHit; // the first colour hit is the nominated one
    return id === ballOn;
}

/** Validate a shooter's report against the state before the shot. Returns an error or null. */
export function validateShot(state: FrameState, report: ShotReport): string | null {
    if (!report || typeof report !== "object") return "invalid report";
    if (state.over) return "frame is over";
    const before = new Set(Object.keys(state.positions));
    if (state.ballInHand) before.add("cue");
    const potted = Array.isArray(report.potted) ? report.potted : null;
    if (!potted) return "invalid potted list";
    if (new Set(potted).size !== potted.length) return "duplicate potted ball";
    for (const id of potted) if (!before.has(id)) return `potted ball ${id} was not on the table`;
    const positions = report.positions;
    if (!positions || typeof positions !== "object") return "invalid positions";
    for (const id of Object.keys(positions)) {
        if (!before.has(id)) return `ball ${id} was not on the table`;
        if (potted.includes(id)) return `ball ${id} is both potted and on the table`;
        const p = positions[id];
        if (!Array.isArray(p) || p.length !== 2 || !p.every(v => typeof v === "number" && isFinite(v))) return `bad position for ${id}`;
        if (!onTable(p)) return `ball ${id} is off the table`;
    }
    for (const id of Array.from(before)) {
        if (!(id in positions) && !potted.includes(id)) return `ball ${id} is missing`;
    }
    if (report.firstHit != null && (!before.has(report.firstHit) || report.firstHit === "cue")) return "invalid first ball hit";
    if (state.ballInHand) {
        if (!report.cueStart || !inD(report.cueStart, 0.02)) return "cue ball must be played from the D";
    }
    return null;
}

/** Apply a validated shot and return the new state plus a summary for the players. */
export function applyShot(prev: FrameState, report: ShotReport): { state: FrameState, summary: ShotSummary } {
    if (rulesetOf(prev) === "anyball") return applyAnyBallShot(prev, report);
    const state: FrameState = JSON.parse(JSON.stringify(prev));
    const player = state.turn;
    const opponent = state.players > 1 ? 1 - player : player;
    const ballOn = state.ballOn;
    const firstHit = report.firstHit ?? null;
    const potted = report.potted.slice();
    const cuePotted = potted.includes("cue");
    const objectPotted = potted.filter(id => id !== "cue");

    state.positions = {};
    for (const id of Object.keys(report.positions)) state.positions[id] = [round(report.positions[id][0]), round(report.positions[id][1])];
    state.shots += 1;

    const foulReasons: string[] = [];
    if (!firstHit) foulReasons.push("no ball hit");
    else if (!legalFirstHit(ballOn, firstHit)) foulReasons.push(`hit the ${ballName(firstHit)} first (ball on: ${describeBallOn(ballOn)})`);
    if (cuePotted) foulReasons.push("cue ball potted");
    const wrongPots = objectPotted.filter(id => !legalPot(ballOn, firstHit, id));
    if (wrongPots.length) foulReasons.push(`potted the ${Array.from(new Set(wrongPots.map(ballName))).join(" and ")}`);
    const foul = foulReasons.length > 0;

    const respotted: string[] = [];
    const respot = (id: Colour) => {
        state.positions[id] = respotPosition(id, state.positions);
        respotted.push(id);
    };

    let points = 0, penalty = 0, turnChanged = false, frameOver = false, blackRespotted = false;
    const s = state.stats;

    if (foul) {
        penalty = Math.max(4, ballOnValue(ballOn, firstHit),
            firstHit ? ballValue(firstHit) : 0,
            ...objectPotted.map(ballValue));
        s[player].fouls += 1;
        if (state.players > 1) s[opponent].score += penalty;
        // Colours potted on a foul go back on their spots; reds stay down.
        for (const id of objectPotted) if (isColour(id)) respot(id);
        // Match: a foul on the last black ends the frame (unless it levels the scores, see below).
        // Practice: it is an ordinary foul and the frame goes on until the black is potted.
        if (ballOn === "black" && state.players > 1) frameOver = true;
        state.breakPoints = 0;
        state.turn = opponent;
        turnChanged = state.players > 1;
        state.ballOn = redsOnTable(state.positions) > 0 ? "red" : (lowestColourOnTable(state.positions) || "black");
    } else if (objectPotted.length > 0) {
        if (ballOn === "red") {
            points = objectPotted.length;
            state.ballOn = "colour";
        } else if (ballOn === "colour") {
            const colour = objectPotted[0] as Colour;
            points = ballValue(colour);
            respot(colour);
            state.ballOn = redsOnTable(state.positions) > 0 ? "red" : "yellow";
        } else {
            points = VALUES[ballOn];
            if (ballOn === "black") frameOver = true;
            else state.ballOn = COLOURS[COLOURS.indexOf(ballOn as Colour) + 1];
        }
        s[player].score += points;
        state.breakPoints += points;
        s[player].highestBreak = Math.max(s[player].highestBreak, state.breakPoints);
    } else {
        state.breakPoints = 0;
        state.turn = opponent;
        turnChanged = state.players > 1;
        state.ballOn = redsOnTable(state.positions) > 0 ? "red" : (lowestColourOnTable(state.positions) || "black");
    }

    state.ballInHand = false;
    if (cuePotted) {
        delete state.positions.cue;
        state.ballInHand = true;
        state.positions.cue = defaultCuePosition(state.positions);
    }

    const breakAfterShot = state.breakPoints; // for the message, before a tie resets it
    if (frameOver) {
        if (state.players > 1 && s[0].score === s[1].score) {
            // Tie on the last black: re-spot it, toss for who plays from hand.
            state.respottedBlack = true;
            blackRespotted = true;
            for (const id of Object.keys(state.positions)) if (id !== "cue") delete state.positions[id];
            state.positions.black = [SPOTS.black[0], SPOTS.black[1]];
            delete state.positions.cue;
            state.positions.cue = defaultCuePosition(state.positions);
            state.ballInHand = true;
            state.ballOn = "black";
            state.breakPoints = 0;
            state.turn = Math.random() < 0.5 ? 0 : 1;
            turnChanged = true;
            frameOver = false;
        } else {
            state.over = true;
            state.winner = state.players > 1 ? (s[0].score > s[1].score ? 0 : 1) : 0;
            state.endReason = foul ? "black-foul" : "black";   // why the frame ended (end-of-frame banner, stored result)
            delete state.positions.black;
            state.ballInHand = false;                          // nobody plays on
            if (foul) respotted.splice(0);                     // the frame is over: nothing goes back on the table
        }
    }

    const summary: ShotSummary = {
        player, foul, foulReasons, penalty, points, potted, respotted,
        turnChanged, ballInHand: state.ballInHand, frameOver: state.over,
        message: "",
    };
    summary.message = describeShot(summary, state, blackRespotted, breakAfterShot);
    return {state, summary};
}

/**
 * Any ball: every object ball is on and worth 1. A foul (no ball hit, or the cue ball potted; -1 once even if both)
 * costs the shooter 1 point; balls potted on a foul still count. The shooter keeps the table after a pot without a
 * foul. Nothing is re-spotted. The frame ends when only the cue ball is left: higher score wins; level scores (match)
 * re-spot the 8 ball on the pink spot (where the rack's apex is) for sudden death (the toss decides who plays from
 * hand), and the frame ends again when it is potted (higher score wins, level again re-spots it again).
 */
/** Any ball sudden death: the ball re-spotted when the table is cleared with the scores level. */
export const SUDDEN_DEATH_BALL = "ball8";

function applyAnyBallShot(prev: FrameState, report: ShotReport): { state: FrameState, summary: ShotSummary } {
    const state: FrameState = JSON.parse(JSON.stringify(prev));
    const player = state.turn;
    const opponent = state.players > 1 ? 1 - player : player;
    const firstHit = report.firstHit ?? null;
    const potted = report.potted.slice();
    const cuePotted = potted.includes("cue");
    const objectPotted = potted.filter(id => id !== "cue");

    state.positions = {};
    for (const id of Object.keys(report.positions)) state.positions[id] = [round(report.positions[id][0]), round(report.positions[id][1])];
    state.shots += 1;
    state.ballOn = "any";

    const foulReasons: string[] = [];
    if (!firstHit) foulReasons.push("no ball hit");
    if (cuePotted) foulReasons.push("cue ball potted");
    const foul = foulReasons.length > 0;
    const points = objectPotted.length;
    const penalty = foul ? 1 : 0;
    const s = state.stats[player];
    s.score += points - penalty;
    if (foul) s.fouls += 1;
    state.breakPoints += points;
    s.highestBreak = Math.max(s.highestBreak, state.breakPoints);
    const breakAfterShot = state.breakPoints;

    let turnChanged = false;
    if (foul || points === 0) {
        state.breakPoints = 0;
        state.turn = opponent;
        turnChanged = state.players > 1;
    }

    state.ballInHand = false;
    if (cuePotted) {
        delete state.positions.cue;
        state.ballInHand = true;
        state.positions.cue = defaultCuePosition(state.positions);
    }

    let suddenDeathStarted = false;
    if (objectBallsOnTable(state.positions) === 0) {
        const scores = state.stats.map(p => p.score);
        if (state.players > 1 && scores[0] === scores[1]) {
            suddenDeathStarted = true;
            state.suddenDeath = true;
            state.positions[SUDDEN_DEATH_BALL] = [SPOTS.pink[0], SPOTS.pink[1]];
            delete state.positions.cue;
            state.positions.cue = defaultCuePosition(state.positions);
            state.ballInHand = true;
            state.breakPoints = 0;
            state.turn = Math.random() < 0.5 ? 0 : 1;
            turnChanged = true;
        } else {
            state.over = true;
            state.winner = state.players > 1 ? (scores[0] > scores[1] ? 0 : 1) : 0;
            state.endReason = "cleared";
            state.ballInHand = false;
        }
    }

    const summary: ShotSummary = {
        player, foul, foulReasons, penalty, points, potted, respotted: suddenDeathStarted ? [SUDDEN_DEATH_BALL] : [],
        turnChanged, ballInHand: state.ballInHand, frameOver: state.over,
        message: "",
    };
    summary.message = describeAnyBallShot(summary, state, suddenDeathStarted, breakAfterShot);
    return {state, summary};
}

function describeAnyBallShot(sm: ShotSummary, state: FrameState, suddenDeathStarted: boolean, breakPoints: number) {
    const parts: string[] = [];
    const pottedNames = sm.potted.filter(id => id !== "cue").map(ballName);
    const pots = pottedNames.length === 1 ? `the ${pottedNames[0]}` : `${pottedNames.length} balls`;
    if (sm.foul) {
        parts.push(`Foul: ${sm.foulReasons.join(", ")}. -1.`);
        if (sm.points > 0) parts.push(`Potted ${pots}: +${sm.points}.`);
    } else if (sm.points > 0) {
        parts.push(`Potted ${pots}: +${sm.points}. Run ${breakPoints}.`);
    } else {
        parts.push("No pot.");
    }
    if (suddenDeathStarted) parts.push("Scores level with the table cleared: the 8 ball is re-spotted for sudden death, the toss decides who plays from hand. Next pot wins.");
    else if (sm.ballInHand) parts.push("Ball in hand.");
    if (state.over) parts.push("Table cleared. Frame over.");
    return parts.join(" ");
}

function describeShot(sm: ShotSummary, state: FrameState, blackRespotted: boolean, breakPoints: number) {
    if (rulesetOf(state) === "anyball") return describeAnyBallShot(sm, state, false, breakPoints);
    const parts: string[] = [];
    if (sm.foul) {
        parts.push(`Foul: ${sm.foulReasons.join(", ")}.` + (state.players > 1 ? ` ${sm.penalty} points to the opponent.` : ` (${sm.penalty} points)`));
        // colours that went down on the foul come back (e.g. black and white together in practice)
        const back = sm.respotted.filter(id => sm.potted.includes(id));
        if (back.length && !state.over) parts.push(`${back.map(ballName).map(n => n[0].toUpperCase() + n.slice(1)).join(" and ")} potted and re-spotted.`);
    } else if (sm.points > 0) {
        const pottedNames = sm.potted.filter(id => id !== "cue").map(ballName);
        parts.push(`Potted ${pottedNames.length > 1 ? pottedNames.length + " reds" : pottedNames[0]}: +${sm.points}. Break ${breakPoints}.`);
    } else {
        parts.push("No pot.");
    }
    if (sm.ballInHand) parts.push("Ball in hand.");
    if (blackRespotted) parts.push("Scores level: the black is re-spotted, the toss decides who plays from hand.");
    if (state.over) parts.push("Frame over.");
    return parts.join(" ");
}

/**
 * The shooter's client could not deliver the result of a shot it started (reload, closed tab, crash, or
 * not back with the shot in time). The shot counts as a foul for "no ball hit": the usual minimum penalty
 * (4, or the value of the ball on if higher) goes to the opponent, the balls go back to where they were
 * before the shot and the opponent plays. Abandoning a shot can therefore never be cheaper than playing it.
 * Any ball: that ruleset's "no ball hit" foul (-1 to the shooter), the balls go back and the opponent plays.
 */
export function abandonShot(prev: FrameState): { state: FrameState, summary: ShotSummary } {
    const positions: Positions = JSON.parse(JSON.stringify(prev.positions));
    const {state, summary} = applyShot(prev, {firstHit: null, potted: [], positions});
    if (!state.over && !(state.respottedBlack && !prev.respottedBlack)) {
        state.positions = positions;            // exactly as before the shot (applyShot rounds)
        state.ballInHand = prev.ballInHand;
    }
    if (state.over && state.endReason === "black-foul") state.endReason = "black-reload";
    summary.foulReasons = ["shot abandoned (the shooter disconnected or reloaded mid-shot)"];
    summary.ballInHand = state.ballInHand;
    summary.message = describeShot(summary, state, state.respottedBlack && !prev.respottedBlack, state.breakPoints);
    return {state, summary};
}

export function concede(prev: FrameState, player: number, reason = "concede"): FrameState {
    const state: FrameState = JSON.parse(JSON.stringify(prev));
    state.over = true;
    state.winner = state.players > 1 ? 1 - player : null;
    state.endReason = reason;
    return state;
}
